import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { envoyerDuDossier, listerIntervenants, listerToutesLesPieces } from './api';

const nomDe = (p) => p.name_company
  || [p.firstname, p.name].filter(Boolean).join(' ') || 'Sans nom';

const estMoi = (p) => p.plateforme?.vous;

/**
 * Envoyer par courriel depuis la plateforme.
 *
 * Les destinataires viennent des intervenants du dossier, chacun en
 * principal, en copie, ou pas du tout ; on peut ajouter une adresse qui n'y
 * figure pas. Le document produit (`document`) part en pièce jointe, les
 * pièces cochées par lien. L'expéditeur reçoit toujours une copie.
 */
export default function EnvoiCourriel({ expertiseId, document = null,
                                        proposes = [], objetPropose = '',
                                        messagePropose = '', piecesJointesParDefaut = false,
                                        libelleBouton = 'Envoyer', onEnvoye }) {
  const [intervenants, setIntervenants] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [roles, setRoles] = useState({});
  const [autre, setAutre] = useState('');
  const [objet, setObjet] = useState(objetPropose);
  const [message, setMessage] = useState(messagePropose);
  const [choisies, setChoisies] = useState([]);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => { setObjet(objetPropose); }, [objetPropose]);
  useEffect(() => { setMessage(messagePropose); }, [messagePropose]);

  useEffect(() => {
    listerIntervenants(expertiseId).then(setIntervenants).catch(() => setIntervenants([]));
    listerToutesLesPieces(expertiseId)
      .then((l) => {
        // Un document déjà envoyé (une déclaration du 12/10…) ne se renvoie
        // pas en pièce : le PDF du jour le remplace.
        const utiles = l.filter((x) => (x.fichier || x.file)
          && !['declaration', 'reclamation'].includes(x.category));
        setPieces(utiles);
        if (piecesJointesParDefaut) setChoisies(utiles.filter((x) => x.joindre).map((x) => x.id));
      })
      .catch(() => setPieces([]));
  }, [expertiseId, piecesJointesParDefaut]);

  // Les adresses proposées (la compagnie du référentiel, par exemple) et
  // celles des intervenants, sans doublon ; soi-même exclu, sa copie est
  // d'office.
  const options = useMemo(() => {
    const vues = new Set();
    const liste = [];
    const ajouter = (email, nom, role, defaut) => {
      const cle = (email || '').trim().toLowerCase();
      if (!cle || vues.has(cle)) return;
      vues.add(cle);
      liste.push({ email: email.trim(), nom, role, defaut });
    };
    proposes.filter((d) => d.role !== 'Vous')
      .forEach((d) => ajouter(d.email, d.nom, d.role, d.copie ? 'cc' : 'a'));
    intervenants.filter((p) => !estMoi(p))
      .forEach((p) => ajouter(p.mail, nomDe(p), p.role1 || '', ''));
    return liste;
  }, [proposes, intervenants]);

  const roleDe = (o) => roles[o.email.toLowerCase()] ?? o.defaut;
  const poser = (o, valeur) => setRoles((r) => ({ ...r, [o.email.toLowerCase()]: valeur }));
  const basculer = (id) => setChoisies((l) => (
    l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  const autres = autre.split(/[,;\s]+/).filter((x) => x.includes('@'));
  const a = [...options.filter((o) => roleDe(o) === 'a').map((o) => o.email), ...autres];
  const cc = options.filter((o) => roleDe(o) === 'cc').map((o) => o.email);
  const pret = a.length > 0 && objet.trim() && (document || choisies.length > 0);

  const envoyer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      const reponse = await envoyerDuDossier(expertiseId, {
        a, cc, objet: objet.trim(), message, pieces: choisies, document,
      });
      setChoisies([]);
      setAutre('');
      onEnvoye?.(reponse);
    } catch (e) {
      setErreur(e?.response?.data?.detail || "Le courriel n'est pas parti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Stack spacing={2}>
      {erreur && <Alert severity="error" onClose={() => setErreur('')}>{erreur}</Alert>}

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Destinataires
        </Typography>
        {options.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Aucun intervenant n'a d'adresse au dossier : saisissez-la ci-dessous,
            ou complétez « Les intervenants ».
          </Typography>
        )}
        <Stack spacing={1}>
          {options.map((o) => (
            <Stack key={o.email} direction={{ xs: 'column', sm: 'row' }} spacing={1}
                   alignItems={{ sm: 'center' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {o.nom}{o.role ? ` — ${o.role}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary"
                            sx={{ wordBreak: 'break-all' }}>
                  {o.email}
                </Typography>
              </Box>
              <TextField select size="small" value={roleDe(o)} sx={{ minWidth: 150 }}
                         onChange={(e) => poser(o, e.target.value)}>
                <MenuItem value="a">Destinataire</MenuItem>
                <MenuItem value="cc">En copie</MenuItem>
                <MenuItem value="">Ne pas envoyer</MenuItem>
              </TextField>
            </Stack>
          ))}
        </Stack>
        <TextField size="small" fullWidth sx={{ mt: 1.5 }}
                   label="Autre adresse (facultatif)" value={autre}
                   onChange={(e) => setAutre(e.target.value)}
                   helperText="Une personne qui n'est pas au dossier. Séparez plusieurs adresses par une virgule." />
      </Box>

      <TextField label="Objet" value={objet} onChange={(e) => setObjet(e.target.value)} />
      <TextField label="Message" value={message} multiline minRows={5}
                 onChange={(e) => setMessage(e.target.value)} />

      {document && (
        <Alert severity="info" icon={false}>
          {document === 'declaration' ? 'Votre déclaration' : 'Votre réclamation'} part
          en PDF joint au courriel, et reste dans vos documents telle qu'elle est partie.
        </Alert>
      )}

      {pieces.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Pièces à transmettre — elles partent par un lien de téléchargement, et
            vous verrez dans le suivi s'il a été ouvert.
          </Typography>
          <Stack sx={{ maxHeight: 280, overflowY: 'auto', mt: 0.5 }}>
            {pieces.map((p) => (
              <FormControlLabel
                key={p.id}
                control={<Checkbox size="small" checked={choisies.includes(p.id)}
                                   onChange={() => basculer(p.id)} />}
                label={`${p.title || 'Sans titre'}${p.category ? ` — ${p.category}` : ''}`} />
            ))}
          </Stack>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        Le courriel part à votre nom ; les réponses vous reviennent, et une copie
        vous est envoyée : gardez-la, c'est votre preuve d'envoi.
      </Typography>

      <Box>
        <Button variant="contained" size="large" onClick={envoyer}
                disabled={occupe || !pret}>
          {occupe ? 'Envoi…' : libelleBouton}
        </Button>
      </Box>
    </Stack>
  );
}
